import {
  DEST_KEYS,
  PERMISSIONS,
  MONTH_COUNT,
  SCHEDULE_SLOTS,
  SEGMENT_KEYS,
  STAGE_IDS,
  STANDARDIZATION_KEYS,
  TASK_GROUP_KEYS,
} from '../domain/constants';
import type {
  Account,
  Allocation,
  AllocationMonth,
  AssignResult,
  Case,
  CaseSaved,
  Dest,
  Permission,
  Reflections,
  Removal,
  Schedule,
  ScheduleRow,
  SegmentKey,
  StageId,
  SessionUser,
  Staff,
  Standardization,
  TaskGroupSetting,
  TaskRow,
} from '../domain/types';
import { isFiniteNumber, sum, toFiniteNumber } from '../lib/safeMath';
import type {
  AccountDto,
  AccountsResponse,
  AllocationReplaceResponse,
  AssignResponse,
  AuthUserDto,
  CaseItemResponse,
  CasesResponse,
  ReflectionsResponse,
  TaskRemovedResponse,
  CaseRemovedResponse,
  StaffRemovedResponse,
  AllocationRemovedResponse,
  ScheduleResponse,
  StaffResponse,
  TaskGroupDto,
  TasksResponse,
} from './schemas';

type Maybe<T> = T | null | undefined;

const cleanText = (value: Maybe<string>): string | null => {
  const text = value?.trim();
  return text === undefined || text === '' ? null : text;
};

/** 'Ｇ' / 'B/G' / ' a ' などから最初の G|A|B|C を取り出す。取れなければ null。 */
export const parseDest = (raw: Maybe<string>): Dest | null => {
  if (raw === null || raw === undefined) return null;
  const letter = /[GABC]/.exec(raw.normalize('NFKC').toUpperCase())?.[0];
  return DEST_KEYS.find((key) => key === letter) ?? null;
};

/** '1' / 1 / '0' → 数値。'-' / '' / 非数 → null。 */
export const parsePriority = (raw: Maybe<string | number>): number | null => toFiniteNumber(raw);

export const parseStandardization = (raw: Maybe<string>): Standardization | null => {
  const text = raw?.normalize('NFKC').trim();
  return STANDARDIZATION_KEYS.find((key) => key === text) ?? null;
};

const parseSegment = (raw: Maybe<string>): SegmentKey | null =>
  SEGMENT_KEYS.find((key) => key === raw) ?? null;

const parseStage = (raw: number): StageId | null => STAGE_IDS.find((id) => id === raw) ?? null;

// ---------- cases ----------
export const mapCases = (response: CasesResponse): Case[] =>
  response.items.map((dto) => ({
    id: dto.id,
    name: dto.name,
    customer: cleanText(dto.customer),
    kind: cleanText(dto.kind),
    segment: parseSegment(dto.segment),
    group: dto.group,
    startMonth: dto.startMonth,
    endMonth: dto.endMonth,
    requiredNinku: dto.requiredNinku,
    status: dto.status,
    dedicatedAgent: dto.dedicatedAgent ?? false,
    owner: cleanText(dto.owner),
  }));

// ---------- staff ----------
export const mapStaff = (response: StaffResponse): Staff[] =>
  response.items.map((dto) => {
    const byMonth: Allocation[][] = Array.from({ length: MONTH_COUNT }, () => []);
    dto.allocations.forEach((a) => {
      byMonth[a.month]?.push({ caseId: a.caseId, percent: a.percent, role: cleanText(a.role) });
    });
    return {
      id: dto.id,
      name: dto.name,
      group: dto.group,
      birthDate: dto.birthDate ?? null,
      workStartDate: dto.workStartDate ?? null,
      age: dto.age ?? null,
      years: dto.yearsOfExperience ?? null,
      canBeAgent: dto.canBeAgent ?? false,
      hasDcExperience: dto.hasDcExperience ?? false,
      qualifications: dto.qualifications ?? [],
      base: cleanText(dto.base),
      byMonth,
    };
  });

// ---------- tasks ----------
const mapGroup = (dto: Maybe<TaskGroupDto>): TaskGroupSetting => ({
  priority1: parsePriority(dto?.priority1),
  priority2: parsePriority(dto?.priority2),
  now: parseDest(dto?.now),
  next: parseDest(dto?.next),
  standardization: parseStandardization(dto?.standardization),
  dx: cleanText(dto?.dx),
  note: cleanText(dto?.note),
});

export const mapTasks = (response: TasksResponse): TaskRow[] =>
  response.items.map((dto) => ({
    id: dto.id,
    phase: dto.phase,
    gyomu: dto.gyomu,
    task: dto.task,
    issue: cleanText(dto.issue),
    hoursPerMonth: dto.hoursPerMonth ?? null,
    groups: {
      hq: mapGroup(dto.groups.hq),
      sen: mapGroup(dto.groups.sen),
      mu: mapGroup(dto.groups.mu),
      ki: mapGroup(dto.groups.ki),
    },
    ideaNo: cleanText(dto.ideaNo),
    ideaMemo: cleanText(dto.ideaMemo),
    ideas: (dto.ideas ?? []).map((idea) => ({
      text: idea.text,
      source: cleanText(idea.source),
      target: cleanText(idea.target),
    })),
  }));

// ---------- schedule ----------
export const mapSchedule = (response: ScheduleResponse): Schedule => ({
  rows: response.rows.map((row) => {
    const stages: (StageId | null)[] = Array.from({ length: SCHEDULE_SLOTS }, () => null);
    row.cells.forEach((cell) => {
      stages[cell.index] = parseStage(cell.stage);
    });
    return { taskId: row.taskId, stages };
  }),
  milestones: response.milestones,
  workingGroups: response.workingGroups.map((wg) => ({
    key: wg.key,
    hqLead: cleanText(wg.hqLead),
    centerLead: cleanText(wg.centerLead),
    members: wg.members,
  })),
  dx: response.dx ? { lead: cleanText(response.dx.lead), members: response.dx.members } : null,
});

// ---------- simulation ----------
const nonNegative = (value: Maybe<number>): number =>
  isFiniteNumber(value) && value > 0 ? value : 0;

export const mapReflections = (response: ReflectionsResponse): Reflections => {
  const [sen, mu, ki] = TASK_GROUP_KEYS.map((key) => nonNegative(response.reflections[key]));
  return { sen: sen ?? 0, mu: mu ?? 0, ki: ki ?? 0 };
};

export const reflectedTotal = (reflections: Reflections): number =>
  sum(TASK_GROUP_KEYS.map((key) => reflections[key]));

// ---------- allocations ----------
export const mapAssignResult = (response: AssignResponse): AssignResult => ({
  assignedMonths: response.assignedMonths,
  totalPercent: response.totalPercent,
});

// ---------- auth ----------
/** 既知の権限だけを残す(未知の文字列は無視する)。 */
const knownPermissions = (raw: readonly string[]): Permission[] =>
  raw.flatMap((value): Permission[] => {
    const known = PERMISSIONS.find((p) => p === value);
    return known === undefined ? [] : [known];
  });

export const mapUser = (dto: AuthUserDto): SessionUser => ({
  id: dto.id,
  name: dto.name,
  permissions: knownPermissions(dto.permissions),
});

// ---------- accounts ----------
export const mapAccountItem = (dto: AccountDto): Account => ({
  id: dto.id,
  name: dto.name,
  permissions: knownPermissions(dto.permissions),
  enabled: dto.enabled ?? true,
});
export const mapAccounts = (response: AccountsResponse): Account[] =>
  response.items.map(mapAccountItem);

// ---------- single-item mappers(書き込み応答用) ----------
const only = <T>(list: readonly T[]): T => {
  const first = list[0];
  if (first === undefined) throw new Error('empty mapping result');
  return first;
};

export const mapCaseItem = (dto: CasesResponse['items'][number]): Case =>
  only(mapCases({ items: [dto] }));
export const mapStaffItem = (dto: StaffResponse['items'][number]): Staff =>
  only(mapStaff({ items: [dto] }));
export const mapTaskItem = (dto: TasksResponse['items'][number]): TaskRow =>
  only(mapTasks({ items: [dto] }));
export const mapScheduleRowItem = (dto: ScheduleResponse['rows'][number]): ScheduleRow =>
  only(mapSchedule({ rows: [dto], milestones: [], workingGroups: [] }).rows);

// ---------- write responses ----------
const NOTHING_REMOVED: Removal = { scheduleRows: 0, allocations: 0, months: 0 };

export const mapTaskRemoved = (dto: TaskRemovedResponse): Removal => ({
  ...NOTHING_REMOVED,
  scheduleRows: dto.removedScheduleRows,
});
export const mapCaseRemoved = (dto: CaseRemovedResponse): Removal => ({
  ...NOTHING_REMOVED,
  allocations: dto.removedAllocations,
});
export const mapStaffRemoved = (dto: StaffRemovedResponse): Removal => ({
  ...NOTHING_REMOVED,
  allocations: dto.removedAllocations,
});
export const mapAllocationRemoved = (dto: AllocationRemovedResponse): Removal => ({
  ...NOTHING_REMOVED,
  months: dto.removedMonths,
});

export const mapCaseSaved = (dto: CaseItemResponse): CaseSaved => ({
  item: mapCaseItem(dto.item),
  trimmedAllocations: dto.trimmedAllocations ?? 0,
});

export const mapAllocationMonths = (dto: AllocationReplaceResponse): AllocationMonth[] =>
  dto.months
    .map((m) => ({ month: m.month, percent: m.percent, role: m.role ?? null }))
    .sort((a, b) => a.month - b.month);
